<template>
  <v-form ref="formRef" v-model="valid">
    <div
      v-for="action in quickFixActions"
      :key="action"
      class="checkbox-wrapper mb-4"
      :class="{ 'checkbox-wrapper--active': isActionSelected(action) }"
    >
      <v-checkbox
        v-model="localValue.item.metadata!.selectedActions"
        :value="action"
        :rules="[atLeastOneIsRequired()]"
      >
        <template #label>
          <span v-html="$t(`ticketDetails.remediationSection.quickFix.${action}`)" />
        </template>
      </v-checkbox>
      <component
        v-if="!!componentsMap[action]"
        ref="actionDetailsForm"
        :is="componentsMap[action]"
        :ticket-details="config.item.ticket"
        :ticket-action="action"
        :disabled="!isActionSelected(action)"
        v-model="(localValue.item.metadata as Record<string, unknown>)[action]"
        @update:modelValue="validate()"
      />
    </div>
  </v-form>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  watch,
  type PropType,
  type Component,
  nextTick,
} from "vue";
import type { VuetifyFormRef } from "@/types.ts";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import { quickFixStepsMap, TicketAction, TicketTrigger } from "@/constants/tickets.ts";
import ContactUserForm from "./forms/ContactUserForm.vue";
import { atLeastOneIsRequired } from "@/_helpers/validators.ts";
import AllowBlockEmailModalForm from "@/components/modals/forms/AllowBlockEmailModalForm.vue";
import type { QuickFixPayload } from "@/_helpers/ticket-helpers.ts";

export default defineComponent({
  components: {
    ContactUserForm,
  },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<QuickFixPayload>>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const quickFixActions = computed(() => {
      return quickFixStepsMap[props.config.item.ticket.eventTrigger as TicketTrigger];
    });

    const componentsMap: { [type in TicketAction]?: Component } = {
      // Removed CONTACT_USER from requirements, but can still be added later
      [TicketAction.CONTACT_USER]: ContactUserForm,
      [TicketAction.DISCARD_EMAIL_WITH_OPTIONS]: AllowBlockEmailModalForm,
    };

    const localValue = ref<DialogDataConfig<QuickFixPayload>>({
      ...props.config,
    });

    const valid = ref(false);
    const childFormValid = ref(true);
    const formRef = ref<VuetifyFormRef>();
    const actionDetailsForm = ref<VuetifyFormRef[]>();

    const validateChildForms = async (): Promise<boolean> => {
      if (!actionDetailsForm.value || actionDetailsForm.value.length === 0) {
        return true;
      }

      const validationPromises = actionDetailsForm.value.map(async (form) => {
        if (form && typeof form.validate === "function") {
          try {
            const result = await form.validate();
            // Handle object return type with valid property
            return result.valid === true;
          } catch (error) {
            console.error("Child form validation error:", error);
            return false;
          }
        }
        return true;
      });

      const results = await Promise.all(validationPromises);
      return results.every((result) => result === true);
    };

    const validate = async () => {
      await nextTick();
      const validationResult = await formRef.value?.validate();
      const childFormsValid = await validateChildForms();
      childFormValid.value = childFormsValid;
      const quickFixFormValid = validationResult && validationResult.valid;
      const overallValid = quickFixFormValid && childFormsValid;
      emit("update:valid", overallValid);
      return overallValid;
    };

    const isActionSelected = (action: TicketAction) => {
      return localValue.value.item.metadata.selectedActions.includes(action);
    };

    watch(
      localValue,
      (newValue) => {
        emit("update:localValue", newValue);
      },
      { deep: true, immediate: true }
    );

    return {
      quickFixActions,
      localValue,
      valid,
      validate,
      validateChildForms,
      formRef,
      TicketAction,
      componentsMap,
      actionDetailsForm,
      childFormValid,
      atLeastOneIsRequired,
      isActionSelected,
    };
  },
});
</script>

<style scoped lang="scss">
.checkbox-wrapper {
  border-radius: 8px;
  border: 2px solid rgb(var(--v-theme-indigo-pale));
  padding: 10px;
  &--active {
    border: 2px solid rgb(var(--v-theme-primary));
  }
}

.sticky-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid rgb(var(--v-theme-indigo-pale)) !important;
  bottom: 0;
  height: 60px;
  padding-top: 15px;
}
</style>
